import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import FolderIcon from '@mui/icons-material/Folder';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from '../../shared/Constants';

interface Project {
  project_id: string;
  name: string;
}

interface MoveToProjectSelectProps {
  open: boolean;
  onClose: () => void;
  chatId: string;
  onMoved: () => void;
}

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const ModalContainer = styled.div<{ $open: boolean }>`
  background: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  border: 1px solid ${({ theme }) => theme.user_menu_border || theme.sidebar_border};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 360px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${({ $open }) => ($open ? 'scale(1)' : 'scale(0.95)')};
  transition: transform 0.2s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.user_menu_divider || theme.sidebar_border};
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Noka', sans-serif;
`;

const IconBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.2rem; }
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.12s ease;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.1rem; opacity: 0.7; }
`;

const EmptyMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.85rem;
`;

const MoveToProjectSelect: React.FC<MoveToProjectSelectProps> = ({
  open, onClose, chatId, onMoved,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [projects, setProjects] = useState<Project[]>([]);

  const fetchProjects = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/list`, {
        headers: { Authorization: `Auth ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (e) {
      console.error('Error fetching projects:', e);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (open) fetchProjects();
  }, [open, fetchProjects]);

  const handleMove = async (projectId: string | null) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: projectId }),
      });
      onMoved();
      onClose();
    } catch (e) {
      console.error('Error moving chat:', e);
    }
  };

  if (!open) return null;

  return createPortal(
    <Overlay $open={open} onClick={onClose}>
      <ModalContainer $open={open} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <FolderIcon style={{ fontSize: '1.2rem', opacity: 0.7 }} />
          <HeaderTitle>Mover para projeto</HeaderTitle>
          <IconBtn onClick={onClose}>
            <CloseIcon />
          </IconBtn>
        </ModalHeader>
        <ListContainer>
          <MenuItem onClick={() => handleMove(null)}>
            <RemoveCircleOutlineIcon />
            <span>Remover do projeto</span>
          </MenuItem>
          {projects.map((p) => (
            <MenuItem key={p.project_id} onClick={() => handleMove(p.project_id)}>
              <FolderIcon />
              <span>{p.name}</span>
            </MenuItem>
          ))}
          {projects.length === 0 && (
            <EmptyMessage>Nenhum projeto criado</EmptyMessage>
          )}
        </ListContainer>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

export default MoveToProjectSelect;
